<template>
  <v-card :loading="cardLoading">
    <v-card-text>
      <v-form
        ref="customFieldsForm"
        v-model="valid"
        class="multi-col-validation ma-4"
        @submit.prevent="handleFormSubmit"
      >
        <v-row no-gutters class="mb-n3">
          <v-col cols="12">
            <v-alert color="info" text class="mb-0">
              <p v-t="'sharedCustomFieldAlert'" class="font-weight-semibold" />
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <draggable
              v-model="customFields"
              v-bind="dragOptions"
              :disabled="!edit"
              :scroll-sensitivity="350"
              :force-fallback="true"
              @end="onDragEnd"
              @start="onDragStart"
            >
              <template v-for="(customField, index) in customFields">
                <h3
                  v-if="index === 0 || (customField.section || 'Inne') !== (customFields[index - 1].section || 'Inne')"
                  :key="`section-header-${index}`"
                  class="text-lg font-weight-semibold mt-4 mb-2"
                >
                  {{ customField.section || 'Inne' }}
                </h3>
                <div
                  :id="`customFieldCard_${index}`"
                  :key="`${index}--custom-field`"
                >
                <v-card outlined class="my-4 card--border-primary">
                  <v-card-title>
                    <div v-if="!edit">{{ customField.name }}</div>
                    <v-text-field
                      v-else
                      v-model="customField.name"
                      outlined
                      dense
                      :label="$t('name')"
                      :placeholder="$t('name')"
                      :rules="[validators.required, validators.whiteSpaceValidator]"
                      maxlength="50"
                      hide-details="auto"
                    >
                      <template v-if="edit" #prepend>
                        <v-icon class="cursor-pointer my-handle" color="primary">{{ icons.mdiDrag }}</v-icon>
                      </template>
                      <template #append-outer>
                        <v-tooltip top transition="scroll-y-transition" open-delay="150">
                          <template #activator="{ on, attrs }">
                            <v-btn color="error" x-small v-bind="attrs" @click="removeCustomField(index)" v-on="on">
                              <v-icon class="me-2" size="16">
                                {{ icons.mdiTrashCanOutline }}
                              </v-icon>
                              <span v-t="'delete'" />
                            </v-btn>
                          </template>
                          <span v-t="'delete'" class="text-xs" />
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-card-title>
                  <v-card-text v-if="edit" class="pt-0">
                    <v-row dense>
                      <v-col cols="12" sm="3">
                        <v-select
                          v-model="customField.fieldType"
                          :items="fieldTypeChoices"
                          :label="$t('fieldType')"
                          outlined
                          dense
                          hide-details="auto"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <v-select
                          v-model="customField.scope"
                          :items="scopeChoices"
                          :label="$t('scope')"
                          outlined
                          dense
                          hide-details="auto"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <v-combobox
                          v-model="customField.section"
                          :items="sectionChoices"
                          :label="$t('section')"
                          outlined
                          dense
                          hide-details="auto"
                          :placeholder="$t('sectionPlaceholder')"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <v-switch
                          v-model="customField.showInSummary"
                          :label="$t('showInSummary')"
                          class="mt-0"
                          hide-details
                          inset
                        />
                        <span class="text-caption text--secondary d-block ps-1 mt-n1">
                          {{ $t('showInSummaryHint') }}
                        </span>
                      </v-col>
                    </v-row>
                    <v-row v-if="customField.fieldType === 'select'" dense class="mt-2">
                      <v-col cols="12">
                        <v-combobox
                          v-model="customField.options"
                          :items="[]"
                          :label="$t('selectOptions')"
                          :placeholder="$t('selectOptionsPlaceholder')"
                          multiple
                          outlined
                          dense
                          hide-details="auto"
                          chips
                          small-chips
                          deletable-chips
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
                </div>
              </template>
            </draggable>
            <v-btn v-if="edit" v-t="'addCustomField'" color="primary" class="my-4" block @click="addCustomField" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h3 class="text-lg font-weight-semibold mb-2">{{ $t('manageSections') }}</h3>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="newSectionName"
                  outlined
                  dense
                  hide-details="auto"
                  :label="$t('newSectionName')"
                  :placeholder="$t('sectionPlaceholder')"
                  @keyup.enter="addSection"
                >
                  <template #append-outer>
                    <v-btn color="primary" small @click="addSection">
                      <v-icon small>{{ icons.mdiPlus }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
            <div class="mt-2 d-flex flex-wrap" style="gap: 8px;">
              <v-chip
                v-for="(section, sIndex) in managedSections"
                :key="section"
                small
                close
                outlined
                color="primary"
                @click:close="removeSection(sIndex)"
              >
                {{ section }}
              </v-chip>
              <span v-if="!managedSections.length" class="text--secondary text-caption">
                {{ $t('noCustomSections') }}
              </span>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="!edit" cols="12"
            ><v-btn color="primary" @click.prevent="edit = !edit">
              <v-icon class="me-2">{{ icons.mdiPencilOutline }}</v-icon> <span v-t="'edit'" /> </v-btn
          ></v-col>
          <v-col v-if="edit" cols="12">
            <v-btn
              color="primary"
              class="me-3"
              type="submit"
              :loading="loading"
              :disabled="!valid || !(areDataChanged || areCustomFieldsOrderChanged)"
            >
              {{ $t('save') }}
            </v-btn>
            <v-btn v-t="'discard'" outlined color="secondary" @click="restoreData" />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-dialog v-model="typeChangeDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon color="warning" class="me-2">{{ icons.mdiAlertOutline }}</v-icon>
          {{ $t('typeChangeWarningTitle') }}
        </v-card-title>
        <v-card-text>
          <p>{{ $t('typeChangeWarningIntro') }}</p>
          <v-alert v-for="impact in typeChangeImpacts" :key="impact.id" color="warning" text dense class="mb-2">
            <div class="font-weight-semibold">{{ impact.name }}</div>
            <div class="text-caption">
              {{ $t('typeChangeWarningCount', { count: impact.incompatible, total: impact.total }) }}
            </div>
            <div v-if="impact.samples && impact.samples.length" class="text-caption mt-1">
              {{ $t('typeChangeWarningSamples') }} {{ impact.samples.join(', ') }}
            </div>
          </v-alert>
          <p class="text-caption text--secondary mb-0">{{ $t('typeChangeWarningHint') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn v-t="'cancel'" text @click="cancelTypeChange" />
          <v-btn v-t="'typeChangeContinue'" color="warning" :loading="loading" @click="confirmTypeChangeAndSave" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteFieldDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon color="error" class="me-2">{{ icons.mdiTrashCanOutline }}</v-icon>
          {{ $t('deleteCustomFieldTitle') }}
        </v-card-title>
        <v-card-text>
          {{ $t('deleteCustomFieldConfirm', { name: fieldPendingDeletionName }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn v-t="'cancel'" text @click="cancelDeleteField" />
          <v-btn v-t="'delete'" color="error" @click="confirmDeleteField" />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mdiDotsVertical, mdiTrashCanOutline, mdiDrag, mdiPencilOutline, mdiPlus, mdiAlertOutline } from '@mdi/js'
import { ref, onMounted, computed, nextTick, getCurrentInstance } from '@vue/composition-api'
import { required, whiteSpaceValidator } from '@core/utils/validation'
import { useActions } from 'vuex-composition-helpers'
import draggable from 'vuedraggable'
import useDraggableOptions from '@/plugins/draggable'

export default {
  components: {
    draggable,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { dragOptions, onDragStart, onDragEnd } = useDraggableOptions()
    const customFieldsForm = ref(null)
    const valid = ref(true)
    const loading = ref(false)
    const cardLoading = ref(false)
    const customFields = ref([])
    let frozenCustomFields = {}

    const edit = ref(false)
    const newSectionName = ref('')
    const managedSections = ref([])

    const {
      getCandidateCustomFields,
      updateCandidateCustomFields,
      getCandidateCustomFieldTypeChangeImpact,
    } = useActions('company', [
      'getCandidateCustomFields',
      'updateCandidateCustomFields',
      'getCandidateCustomFieldTypeChangeImpact',
    ])

    const typeChangeDialog = ref(false)
    const typeChangeImpacts = ref([])
    const deleteFieldDialog = ref(false)
    const fieldPendingDeletionIndex = ref(null)
    const fieldPendingDeletionName = ref('')

    const sortCustomFieldsBySection = () => {
      customFields.value.sort((a, b) => {
        const sa = (a.section || 'Inne').toLowerCase()
        const sb = (b.section || 'Inne').toLowerCase()
        return sa.localeCompare(sb)
      })
    }

    onMounted(async () => {
      cardLoading.value = true
      customFields.value = await getCandidateCustomFields()
      sortCustomFieldsBySection()
      frozenCustomFields = Object.freeze(_.cloneDeep(customFields.value))
      const sections = new Set()
      customFields.value.forEach(cf => {
        if (cf.section) sections.add(cf.section)
      })
      managedSections.value = Array.from(sections).sort()
      cardLoading.value = false
    })

    const addSection = () => {
      const name = newSectionName.value.trim()
      if (name && !managedSections.value.includes(name)) {
        managedSections.value.push(name)
        managedSections.value.sort()
      }
      newSectionName.value = ''
    }

    const removeSection = (index) => {
      managedSections.value.splice(index, 1)
    }

    const doSave = async () => {
      loading.value = true
      sortCustomFieldsBySection()
      customFields.value = await updateCandidateCustomFields(customFields.value)
      frozenCustomFields = Object.freeze(_.cloneDeep(customFields.value))
      loading.value = false
      edit.value = false
    }

    const getChangedTypeFields = () => {
      if (!Array.isArray(frozenCustomFields)) return []
      const originalById = {}
      frozenCustomFields.forEach(cf => {
        if (cf.id) originalById[cf.id] = cf
      })

      return customFields.value.filter(cf => {
        const original = cf.id ? originalById[cf.id] : null
        return original && original.fieldType !== cf.fieldType
      })
    }

    const handleFormSubmit = async () => {
      const isFormValid = customFieldsForm.value.validate()
      if (!isFormValid) return

      const changedTypeFields = getChangedTypeFields()
      if (changedTypeFields.length) {
        loading.value = true
        try {
          const impacts = await Promise.all(
            changedTypeFields.map(async cf => {
              const result = await getCandidateCustomFieldTypeChangeImpact({
                fieldId: cf.id,
                newType: cf.fieldType,
              })

              return {
                id: cf.id,
                name: cf.name,
                incompatible: result.incompatibleValues,
                total: result.totalValues,
                samples: result.sampleIncompatible || [],
              }
            }),
          )
          loading.value = false

          const affected = impacts.filter(impact => impact.incompatible > 0)
          if (affected.length) {
            typeChangeImpacts.value = affected
            typeChangeDialog.value = true

            return
          }
        } catch (e) {
          loading.value = false
        }
      }

      await doSave()
    }

    const confirmTypeChangeAndSave = async () => {
      typeChangeDialog.value = false
      await doSave()
    }

    const cancelTypeChange = () => {
      typeChangeDialog.value = false
      typeChangeImpacts.value = []
    }

    const restoreData = () => {
      customFields.value = _.cloneDeep(frozenCustomFields)
      edit.value = false
    }

    const fieldTypeChoices = computed(() => [
      { text: vm.$t('customFieldTypeText'), value: 'text' },
      { text: vm.$t('customFieldTypeDate'), value: 'date' },
      { text: vm.$t('customFieldTypeBoolean'), value: 'boolean' },
      { text: vm.$t('customFieldTypeSelect'), value: 'select' },
    ])

    const scopeChoices = computed(() => [
      { text: vm.$t('customFieldScopeBoth'), value: 'both' },
      { text: vm.$t('customFieldScopeEmployee'), value: 'employee' },
      { text: vm.$t('customFieldScopeCandidate'), value: 'candidate' },
    ])

    const sectionChoices = computed(() => {
      const sections = new Set(managedSections.value)
      customFields.value.forEach(cf => {
        if (cf.section) sections.add(cf.section)
      })
      if (frozenCustomFields && Array.isArray(frozenCustomFields)) {
        frozenCustomFields.forEach(cf => {
          if (cf.section) sections.add(cf.section)
        })
      }
      return Array.from(sections).sort()
    })

    const addCustomField = async () => {
      customFields.value.push({
        name: '',
        fieldType: 'text',
        scope: 'both',
        section: 'Inne',
        showInSummary: false,
      })
      nextTick(() => {
        const el = document.getElementById(`customFieldCard_${customFields.value.length - 1}`)
        el.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        })
      })
    }

    const removeCustomField = (index) => {
      const field = customFields.value[index]
      // New (unsaved) fields have no persisted values, so skip the warning.
      if (!field || !field.id) {
        customFields.value.splice(index, 1)

        return
      }
      fieldPendingDeletionIndex.value = index
      fieldPendingDeletionName.value = field.name
      deleteFieldDialog.value = true
    }

    const confirmDeleteField = () => {
      if (fieldPendingDeletionIndex.value !== null) {
        customFields.value.splice(fieldPendingDeletionIndex.value, 1)
      }
      deleteFieldDialog.value = false
      fieldPendingDeletionIndex.value = null
      fieldPendingDeletionName.value = ''
    }

    const cancelDeleteField = () => {
      deleteFieldDialog.value = false
      fieldPendingDeletionIndex.value = null
      fieldPendingDeletionName.value = ''
    }

    const areDataChanged = computed(() => {
      return (
        !_.isEmpty(_.differenceWith(customFields.value, frozenCustomFields, _.isEqual)) ||
        !_.isEmpty(_.differenceWith(frozenCustomFields, customFields.value, _.isEqual))
      )
    })

    const areCustomFieldsOrderChanged = computed(() => {
      // eslint-disable-next-line array-callback-return
      const customFieldsOrder = customFields.value.map((cf) => {
        if (Object.hasOwnProperty.bind(cf)('order')) return cf.order
      })

      return !customFieldsOrder.every((el, index) => {
        return el === frozenCustomFields[index]?.order
      })
    })

    return {
      handleFormSubmit,
      confirmTypeChangeAndSave,
      cancelTypeChange,
      confirmDeleteField,
      cancelDeleteField,
      restoreData,
      addCustomField,
      removeCustomField,
      onDragStart,
      onDragEnd,
      addSection,
      removeSection,

      typeChangeDialog,
      typeChangeImpacts,
      deleteFieldDialog,
      fieldPendingDeletionName,

      areDataChanged,
      areCustomFieldsOrderChanged,

      validators: {
        required,
        whiteSpaceValidator,
      },
      customFieldsForm,
      valid,
      loading,
      cardLoading,
      customFields,
      dragOptions,
      edit,
      fieldTypeChoices,
      scopeChoices,
      sectionChoices,
      newSectionName,
      managedSections,
      icons: {
        mdiDotsVertical,
        mdiTrashCanOutline,
        mdiDrag,
        mdiPencilOutline,
        mdiPlus,
        mdiAlertOutline,
      },
    }
  },
}
</script>

<style lang="scss" scoped></style>
